import {
  STAT_CIRCLE_SWIRL_ROTATE_POD,
  STAT_CIRCLE_SWIRL_ROTATE_STAT,
  SWIRL_3_SVG_HEIGHT,
  SWIRL_3_SVG_PATH,
  SWIRL_3_SVG_WIDTH,
  SWIRL_4_SVG_HEIGHT,
  SWIRL_4_SVG_PATH,
  SWIRL_4_SVG_WIDTH,
} from '@/constants/svg';

import styles from './StatCircle.module.css';

type StatCircleColor = 'blue' | 'green' | 'orange' | 'red';
type StatCircleVariant = 'solid' | 'ring' | 'pod';
type StatCircleFigure = 'stroke' | 'fill';
type StatCircleContent = 'hero' | 'stat' | 'compact';

type StatCircleProps = {
  /** Chapter palette variant. Omit to inherit the ambient chapter accent. */
  color?: StatCircleColor;
  /**
   * Visual chrome. `solid` — two-tone badge (default). `ring` — translucent
   * swirl-3. `pod` — opaque swirl-4 graph-stat disk.
   */
  variant?: StatCircleVariant;
  /** Figure treatment for Value. Defaults: solid/ring → stroke, pod → fill. */
  figure?: StatCircleFigure;
  /**
   * Typography scale. Defaults: solid/ring → stat (72px + Label L),
   * pod → compact (36px + Body S). Use `hero` for Arctic Tern (96px + Subtitle).
   */
  content?: StatCircleContent;
  /** Swirl/badge rotation in degrees. Defaults: solid 0, ring −45, pod −150. */
  swirlRotate?: number;
  /** Opacity multiplier on the swirl image (badge SVG ignores this). */
  swirlOpacity?: number;
  /** Compose with `<StatCircle.Value>` / `<StatCircle.Caption>` or custom content. */
  children: React.ReactNode;
  className?: string;
};

const colorClass: Record<StatCircleColor, string> = {
  blue: styles.blue,
  green: styles.green,
  orange: styles.orange,
  red: styles.red,
};

const variantClass: Record<StatCircleVariant, string> = {
  solid: '',
  ring: styles.ring,
  pod: styles.pod,
};

const contentClass: Record<StatCircleContent, string> = {
  hero: styles.contentHero,
  /** Base `.value` / `.caption` rules are the `stat` preset — no extra class. */
  stat: '',
  compact: styles.contentCompact,
};

const figureClass: Record<StatCircleFigure, string> = {
  stroke: styles.figureStroke,
  fill: styles.figureFill,
};

function defaultFigure(variant: StatCircleVariant): StatCircleFigure {
  return variant === 'pod' ? 'fill' : 'stroke';
}

function defaultContent(variant: StatCircleVariant): StatCircleContent {
  return variant === 'pod' ? 'compact' : 'stat';
}

function defaultSwirlRotate(variant: StatCircleVariant): number {
  // Solid badge path is authored upright (two-tone diagonal already correct).
  // Ring/pod Figma assets are drawn rotated and need −45° / −150°.
  if (variant === 'pod') return STAT_CIRCLE_SWIRL_ROTATE_POD;
  if (variant === 'ring') return STAT_CIRCLE_SWIRL_ROTATE_STAT;
  return 0;
}

/** Solid badge disk with baked-in swash arcs — Figma vector 1085:48547. */
const BADGE_PATH =
  'M294.239 255.578C333.748 193.455 328.912 136.332 314.677 98.3298C304.961 72.0904 290.376 55.2424 285.628 50.2673C285.062 49.702 284.61 49.2497 284.045 48.6844C219.145 -16.2158 113.829 -16.2783 48.7532 48.7975C41.9627 55.5881 35.7375 62.9439 30.4172 70.5256C3.70173 109.905 6.51317 144.405 12.7195 176.189C20.8444 217.361 39.1544 249.931 39.1544 249.931C16.0922 219.852 6.26136 197.572 0.621088 164.77C0.508457 163.752 0.282648 162.847 0.0569502 161.716C-1.09547 205.833 15.1725 250.395 48.8663 284.089C113.767 348.989 219.082 349.052 284.158 283.976C321.845 246.288 337.707 195.15 331.622 146.059C332.621 186.442 318.008 227.965 294.239 255.578ZM314.084 154.099C314.322 129.664 310.824 111.454 310.824 111.454C317.827 134.753 317.922 172.422 310.784 195.842C310.784 195.842 313.846 178.533 314.084 154.099Z';

/**
 * The badge's two-tone gradient reads the palette custom properties from its
 * own DOM position. `url(#id)` always resolves against the *first* element
 * with that id in the document, so the id must be unique per palette or a
 * green badge would pick up the blue badge's resolved gradient.
 */
function badgeGradientId(color: StatCircleColor | undefined): string {
  return color ? `stat-circle-badge-${color}` : 'stat-circle-badge-accent';
}

function StatCircleBadge({ color }: { color?: StatCircleColor }) {
  const gradientId = badgeGradientId(color);
  return (
    <svg
      viewBox="0 0 333 333"
      width={333}
      height={333}
      fill="none"
      aria-hidden
      className={styles.swirlArt}
    >
      <path d={BADGE_PATH} fill={`url(#${gradientId})`} />
      <defs>
        <linearGradient
          id={gradientId}
          x1="52.5005"
          y1="286.432"
          x2="287"
          y2="55.4316"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.5" style={{ stopColor: 'var(--stat-circle-bg)' }} />
          <stop offset="0.5" style={{ stopColor: 'var(--stat-circle-tint)' }} />
        </linearGradient>
      </defs>
    </svg>
  );
}

function StatCircleSwirl({
  src,
  width,
  height,
  opacity,
}: {
  src: string;
  width: number;
  height: number;
  opacity: number;
}) {
  return (
    // eslint-disable-next-line @next/next/no-img-element -- decorative SVG asset (ADR-004)
    <img
      src={src}
      alt=""
      aria-hidden
      width={width}
      height={height}
      className={styles.swirlArt}
      style={opacity === 1 ? undefined : { opacity }}
      decoding="async"
    />
  );
}

/**
 * Circular swirl callout (server component — pure CSS/SVG). For in-view fade +
 * spring-badge scale-in, wrap in `<StatCircleReveal>` (Motion leaf island).
 * Without a wrapper (or under reduced motion) it renders visible and static.
 *
 * Size via the `--stat-circle-size` custom property (default 20rem).
 */
export function StatCircle({
  color,
  variant = 'solid',
  figure: figureProp,
  content: contentProp,
  swirlRotate: swirlRotateProp,
  swirlOpacity = 1,
  children,
  className,
}: StatCircleProps) {
  const figure = figureProp ?? defaultFigure(variant);
  const content = contentProp ?? defaultContent(variant);
  const swirlRotate = swirlRotateProp ?? defaultSwirlRotate(variant);

  return (
    <div
      className={[
        styles.root,
        variantClass[variant],
        contentClass[content],
        figureClass[figure],
        color ? colorClass[color] : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        {
          '--stat-swirl-rotate': `${swirlRotate}deg`,
        } as React.CSSProperties
      }
    >
      <div className={styles.swirlWrap}>
        {variant === 'ring' ? (
          <StatCircleSwirl
            src={SWIRL_3_SVG_PATH}
            width={SWIRL_3_SVG_WIDTH}
            height={SWIRL_3_SVG_HEIGHT}
            opacity={swirlOpacity}
          />
        ) : null}
        {variant === 'pod' ? (
          <StatCircleSwirl
            src={SWIRL_4_SVG_PATH}
            width={SWIRL_4_SVG_WIDTH}
            height={SWIRL_4_SVG_HEIGHT}
            opacity={swirlOpacity}
          />
        ) : null}
        {variant === 'solid' ? <StatCircleBadge color={color} /> : null}
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  );
}

type StatCircleSlotProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

function StatCircleValue({ children, className, style }: StatCircleSlotProps) {
  return (
    <span className={[styles.value, className].filter(Boolean).join(' ')} style={style}>
      {children}
    </span>
  );
}

function StatCircleCaption({ children, className, style }: StatCircleSlotProps) {
  return (
    <span className={[styles.caption, className].filter(Boolean).join(' ')} style={style}>
      {children}
    </span>
  );
}

StatCircle.Value = StatCircleValue;
StatCircle.Caption = StatCircleCaption;
